/**
 * HomePage — S04 dashboard (Phase I of the design-takeover).
 *
 * Route: /home
 *
 * Layout:
 *   - Left rail: pair switcher, keyboard-navigable listbox.
 *   - Main area: "Next session" card, recent activity, goals tracker.
 *
 * Data: driven by listMyPairs (Phase C) via the appsyncQuery client.
 *
 * A11y:
 *   - Pair switcher is a <nav> with role="listbox" keyboard nav.
 *   - Main area is a <main> landmark (or nested when App.jsx provides one).
 *   - Dashboard cards are <section> elements with aria-labelledby.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { appsyncQuery } from '../api/appsync-client.js'
import { useSessions } from '../hooks/useSessions.js'
import { Button, CardFrame, EmptyState, PageContainer } from '../ui'

// ─── GraphQL ──────────────────────────────────────────────────────────────────

const LIST_MY_PAIRS = `
  query ListMyPairs {
    listMyPairs {
      pairId
      reportId
      managerId
      status
      cadence
      reportName
      reportEmail
      managerName
      managerEmail
    }
  }
`

// ─── PairSwitcher ─────────────────────────────────────────────────────────────

/**
 * Left-rail pair list. Keyboard-navigable with arrow keys (a11y gate).
 * Only shows active pairs; non-active pairs are filtered out.
 */
function PairSwitcher({ pairs, selectedPairId, onSelect }) {
  const listRef = useRef(null)

  function handleKeyDown(e, pairId, index) {
    const items = listRef.current?.querySelectorAll('[role="option"]')
    if (!items) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      const next = items[Math.min(index + 1, items.length - 1)]
      next?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prev = items[Math.max(index - 1, 0)]
      prev?.focus()
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelect(pairId)
    }
  }

  if (!pairs.length) {
    return (
      <nav className="home__pair-switcher home__pair-switcher--empty" aria-label="Pairs">
        <p className="home__pair-switcher-empty">No active pairs yet.</p>
      </nav>
    )
  }

  return (
    <nav className="home__pair-switcher" aria-label="Pairs">
      <h2 className="home__pair-switcher-title">My pairs</h2>
      <ul
        ref={listRef}
        role="listbox"
        aria-label="Select a pair"
        aria-activedescendant={selectedPairId ? `pair-option-${selectedPairId}` : undefined}
        className="home__pair-list"
      >
        {pairs.map((pair, index) => {
          const label = pair.reportName || pair.reportEmail || pair.managerId
          const isSelected = pair.pairId === selectedPairId
          return (
            <li
              key={pair.pairId}
              id={`pair-option-${pair.pairId}`}
              role="option"
              aria-selected={isSelected}
              tabIndex={0}
              className={`home__pair-item${isSelected ? ' home__pair-item--selected' : ''}`}
              onClick={() => onSelect(pair.pairId)}
              onKeyDown={(e) => handleKeyDown(e, pair.pairId, index)}
            >
              <span className="home__pair-item-label">{label}</span>
              {pair.cadence && (
                <span className="home__pair-item-cadence" aria-label={`cadence: ${pair.cadence}`}>
                  {pair.cadence}
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

// ─── NextSessionCard ─────────────────────────────────────────────────────────

function NextSessionCard({ pair, navigate }) {
  if (!pair) return null
  const partnerName = pair.reportName || pair.reportEmail || pair.managerId

  return (
    <CardFrame
      as="section"
      kind="overview"
      title="Next session"
      aria-label="Next session"
    >
      <p className="home__next-session-partner">
        With <strong>{partnerName}</strong> · {pair.cadence}
      </p>
      <Button
        variant="primary"
        onClick={() => navigate('/home')}
        aria-label={`Start prep for session with ${partnerName}`}
      >
        Start prep
      </Button>
    </CardFrame>
  )
}

// ─── RecentActivityCard ───────────────────────────────────────────────────────

function RecentActivityCard({ pair }) {
  if (!pair) return null
  return (
    <CardFrame
      as="section"
      kind="overview"
      title="Recent activity"
      aria-label="Recent activity"
    >
      <p className="home__placeholder-text">
        Recent session activity will appear here.
      </p>
    </CardFrame>
  )
}

// ─── GoalsTrackerCard ─────────────────────────────────────────────────────────

function GoalsTrackerCard({ pair, navigate }) {
  if (!pair) return null
  return (
    <CardFrame
      as="section"
      kind="overview"
      title="Goals"
      aria-label="Goals"
    >
      <p className="home__placeholder-text">
        Active goals for this pair will appear here.
      </p>
      <Button
        variant="ghost"
        onClick={() => navigate('/goals')}
        aria-label="View all goals"
      >
        View goals
      </Button>
    </CardFrame>
  )
}

// ─── HomePage ─────────────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {string|null} props.initialPairId - Pre-selected pairId from URL params.
 */
export function HomePage({ initialPairId = null }) {
  const navigate = useNavigate()
  const [pairs, setPairs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedPairId, setSelectedPairId] = useState(initialPairId)
  const [soloLoading, setSoloLoading] = useState(false)

  const { createSession } = useSessions({ enabled: false })

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    appsyncQuery(LIST_MY_PAIRS)
      .then((data) => {
        if (cancelled) return
        const activePairs = (data?.listMyPairs ?? []).filter(
          (p) => p.status === 'active'
        )
        setPairs(activePairs)
        // Auto-select first pair if no initial selection
        if (!initialPairId && activePairs.length > 0) {
          setSelectedPairId(activePairs[0].pairId)
        }
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [initialPairId])

  const selectedPair = pairs.find((p) => p.pairId === selectedPairId) ?? null

  const handleStartSoloPrep = useCallback(async () => {
    setSoloLoading(true)
    try {
      // No managerId — this is a pairless (solo) session.
      const session = await createSession({})
      navigate(`/sessions/${session.sessionId}`)
    } finally {
      setSoloLoading(false)
    }
  }, [createSession, navigate])

  function handleSelectPair(pairId) {
    setSelectedPairId(pairId)
    // Update URL to reflect selected pair (no full navigation)
    navigate(`/home?pair=${pairId}`, { replace: true })
  }

  if (loading) {
    return (
      <div className="home__loading" aria-busy="true" aria-live="polite">
        Loading your dashboard…
      </div>
    )
  }

  if (error) {
    return (
      <div className="home__error" role="alert">
        Failed to load your dashboard. Please try again.
      </div>
    )
  }

  if (pairs.length === 0) {
    return (
      <PageContainer as="main" className="home home--empty">
        <EmptyState
          title="No pairs yet"
          body="Start a solo prep session to get going on your own, or pair up with a manager or direct report to prep together."
          action={
            <Button
              variant="primary"
              onClick={handleStartSoloPrep}
              disabled={soloLoading}
            >
              {soloLoading ? 'Starting…' : 'Start solo prep'}
            </Button>
          }
        />
      </PageContainer>
    )
  }

  return (
    <div className="home">
      <PairSwitcher
        pairs={pairs}
        selectedPairId={selectedPairId}
        onSelect={handleSelectPair}
      />
      <main className="home__content" aria-label="Dashboard">
        <PageContainer>
          <h1 className="home__title">Dashboard</h1>
          <div className="home__cards" data-testid="home-cards">
            <NextSessionCard pair={selectedPair} navigate={navigate} />
            <RecentActivityCard pair={selectedPair} />
            <GoalsTrackerCard pair={selectedPair} navigate={navigate} />
          </div>
        </PageContainer>
      </main>
    </div>
  )
}
